import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, InputBase, Paper, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChatInterface = forwardRef(function ChatInterface({
  messages = [],
  chatId,
  onMessagesChange,
  interactionId,
  onInteractionId,
  streamUrl = '/api/agent/chat',
  compact = false,
}, ref) {
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const messagesRef = useRef(messages);
  const chatIdRef = useRef(chatId);
  const [value, setValue] = useState('');

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    clear: () => setValue(''),
  }), []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isStreaming = messages.some((message) => message.role === 'assistant' && message.streaming);

  const submitMessage = (event) => {
    event.preventDefault();
    if (isStreaming) return;
    const text = value.trim();
    if (!text) return;
    const currentChatId = chatIdRef.current || `${Date.now()}`;
    chatIdRef.current = currentChatId;
    setValue('');
    const assistantMessage = { role: 'assistant', text: '', streaming: true, loading: true };
    messagesRef.current = [...messagesRef.current, { role: 'user', text }, assistantMessage];
    onMessagesChange?.(messagesRef.current, currentChatId);
    void streamMessage(text, interactionId, currentChatId);
  };

  const updateMessages = (update, currentChatId = chatIdRef.current) => {
    messagesRef.current = update(messagesRef.current);
    onMessagesChange?.(messagesRef.current, currentChatId);
  };

  const streamMessage = async (prompt, previousInteractionId, currentChatId) => {
    try {
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: prompt,
          previous_interaction_id: previousInteractionId,
        }),
      });
      if (!response.ok || !response.body) {
        throw new Error(`AI request failed (${response.status}).`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';
        for (const event of events) {
          const line = event.split('\n').find((part) => part.startsWith('data: '));
          if (!line) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.type === 'text') {
            updateMessages((currentMessages) => currentMessages.map((message, index) => (
              index === currentMessages.map((item) => item.role).lastIndexOf('assistant')
                ? { ...message, text: `${message.text}${payload.text}`, loading: false }
                : message
            )), currentChatId);
          } else if (payload.type === 'status') {
            updateMessages((currentMessages) => {
              const assistantIndex = currentMessages.map((message) => message.role).lastIndexOf('assistant');
              const nextMessages = [...currentMessages];
              nextMessages[assistantIndex] = { ...nextMessages[assistantIndex], loading: false };
              nextMessages.splice(assistantIndex, 0, {
                role: 'agent_step',
                text: payload.text,
                arguments: payload.arguments || {},
              });
              return nextMessages;
            }, currentChatId);
          } else if (payload.type === 'done') {
            onInteractionId?.(payload.interaction_id, currentChatId);
            updateMessages((currentMessages) => currentMessages.map((message, index) => (
              index === currentMessages.map((item) => item.role).lastIndexOf('assistant')
                ? { ...message, streaming: false, loading: false }
                : message
            )), currentChatId);
          } else if (payload.type === 'error') {
            throw new Error(payload.message);
          }
        }
        if (done) break;
      }
    } catch (error) {
      updateMessages((currentMessages) => currentMessages.map((message, index) => (
        index === currentMessages.map((item) => item.role).lastIndexOf('assistant')
          ? { ...message, text: error.message, streaming: false, loading: false }
          : message
      )), currentChatId);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box role="log" aria-label="AI conversation" data-chat-id={chatId || undefined} sx={{ flex: 1, overflowY: 'auto', py: compact ? 1 : 3 }}>
        {messages.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <AutoAwesomeIcon color="primary" fontSize={compact ? 'medium' : 'large'} />
            <Typography variant={compact ? 'body1' : 'h6'}>How can I help?</Typography>
            {!compact && <Typography color="text.secondary">Ask about your samples, protocols, or experiments.</Typography>}
          </Box>
        ) : (
          <>
            {messages.map((message, index) => (
              message.role === 'assistant' && !message.text && !message.loading ? null : (
              <Box
                key={`${message.role}-${index}`}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: message.role === 'user'
                      ? 'primary.main'
                      : message.role === 'agent_step' ? 'action.selected' : 'action.hover',
                    color: message.role === 'user'
                      ? 'primary.contrastText'
                      : message.role === 'agent_step' ? 'text.secondary' : 'text.primary',
                    fontSize: message.role === 'agent_step' ? '0.875rem' : undefined,
                    fontStyle: message.role === 'agent_step' ? 'italic' : undefined,
                    whiteSpace: message.role === 'user' || (message.role === 'assistant' && message.streaming)
                      ? 'pre-wrap'
                      : 'normal',
                    '& p': { m: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },
                    '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
                    '& li': { mb: 0.5 },
                    '& pre': {
                      overflowX: 'auto',
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    },
                    '& code': {
                      fontFamily: 'monospace',
                    },
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      my: 1,
                    },
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 1,
                      py: 0.5,
                      textAlign: 'left',
                    },
                    '& th': {
                      fontWeight: 'bold',
                    },
                  }}
                >
                  {message.role === 'user' ? message.text : message.role === 'agent_step' ? (
                    <details>
                      <summary style={{ cursor: 'pointer' }}>{message.text}</summary>
                      <Box
                        component="pre"
                        sx={{
                          mt: 1,
                          mb: 0,
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.75rem',
                          fontStyle: 'normal',
                        }}
                      >
                        {JSON.stringify(message.arguments || {}, null, 2)}
                      </Box>
                    </details>
                  ) : message.loading ? (
                    <CircularProgress size={18} color="inherit" aria-label="Assistant is thinking" />
                  ) : message.streaming ? (
                    message.text
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
                  )}
                </Box>
              </Box>
              )
            ))}
            <Box ref={bottomRef} />
          </>
        )}
      </Box>
      <Paper component="form" onSubmit={submitMessage} variant="outlined" sx={{ display: 'flex', alignItems: 'center', mb: compact ? 0 : 2, px: 1 }}>
        <InputBase
          value={value}
          onChange={(event) => setValue(event.target.value)}
          inputRef={inputRef}
          placeholder="Message the AI assistant…"
          aria-label="Message the AI assistant"
          sx={{ flex: 1, px: 1, py: 0.75 }}
        />
        <IconButton type="submit" aria-label="Send message" disabled={isStreaming || !value?.trim()} color="primary">
          <SendIcon />
        </IconButton>
      </Paper>
    </Box>
  );
});

export default ChatInterface;
